import {
  IDeprecatedEntity,
  IEntity,
  IEntityTotalProgress,
  IHttpsCallableFailResponse,
  IHttpsCallableSuccessResponse,
  Modify,
  UserType,
  UUID
} from "../../../src/app/core/interfaces";
import { Timestamp } from "firebase-admin/firestore";

export interface IGetTotalEntityProgressReq {
  entityId: UUID;
  orgId: UUID | null;
}
export interface IGetTotalEntityProgressSuccessRes extends IHttpsCallableSuccessResponse {
  entityTotalProgress: IEntityTotalProgress
}
export type IGetTotalEntityProgressRes = IGetTotalEntityProgressSuccessRes | IHttpsCallableFailResponse;

export interface ICreateReportReq {
  entityId: UUID;
  orgId: UUID | null;
  currentDate: string;
}
export interface ICreateReportSuccessRes extends IHttpsCallableSuccessResponse {
  htmlTemplate: string;
}
export type ICreateReportRes = ICreateReportSuccessRes | IHttpsCallableFailResponse;

export interface ICreateSnapshotReportReq {
  entityId: UUID;
  processId: UUID;
  currentDate: string;
}
export interface ICreateSnapshotReportSuccessRes extends IHttpsCallableSuccessResponse {
  htmlTemplate: string;
}
export type ICreateSnapshotReportRes = ICreateSnapshotReportSuccessRes | IHttpsCallableFailResponse;

/**
* @deprecated since owner re-structuring into their own collection
*/
export interface ICreateDeprecatedReportReq {
  entityData: IDeprecatedEntity;
  currentDate: string;
}
/**
* @deprecated since owner re-structuring into their own collection
*/
export interface ICreateDeprecatedReportSuccessRes extends IHttpsCallableSuccessResponse {
  htmlTemplate: string;
}
/**
* @deprecated since owner re-structuring into their own collection
*/
export type ICreateDeprecatedReportRes = ICreateDeprecatedReportSuccessRes | IHttpsCallableFailResponse;

export interface ISendSecureFileTransferEmailReq {
  email: string;
  ownerName: string;
  entityId: UUID;
  entityName: string;
  orgId: UUID;
  orgName: string;
}

export interface IValidateSecureFileInvitationTokenSuccessResponse extends IHttpsCallableSuccessResponse {
  payload: ISendSecureFileTransferEmailReq;
}
export type IValidateSecureFileInvitationTokenRes =
  | IValidateSecureFileInvitationTokenSuccessResponse
  | IHttpsCallableFailResponse;

export interface ISendInstantlyTransferEmailReq {
  orgName: string;
  email: string;
  displayName: string;
  entityId: UUID;
  entityName: string;
}

export interface IRevokeSecureFileInviteReq {
  orgId: UUID;
  entityId: UUID;
}

export interface IUpdateEntityDetailsWithTransferReq {
  orgId: UUID;
  entityId: UUID;
}

export interface IMirrorEntityFromUserReq {
  userType: UserType;
  entityId: UUID;
  orgId?: UUID;
}

export interface IMirrorEntityProgressFromUserReq {
  userType: UserType;
  entityId: UUID;
  orgId?: UUID;
}

export interface IMirrorEntityToGroupReq {
  entityId: UUID;
  entityLegalName: string;
  orgId: UUID;
  groupId: UUID;
}

export interface IMirrorDeleteEntityFromUserReq {
  userType: UserType;
  entityId: UUID;
  orgId?: UUID;
}

export interface ILinkUserToEntityReq {
  ownerName: string;
  entityId: UUID;
  entityName: string;
  orgId: UUID;
  orgName: string;
  taxId: string;
}

export interface IFailedAcceptanceAttemptsCf {
  failedAcceptanceAttempts: number;
}

export type IEntityCf = Modify<IEntity, {
  formationDate: Timestamp | null;
  dueDate: Timestamp | null;
  lastAcceptedFiling: Timestamp | null;
  lastModifiedDate: Timestamp | null;
  bulkUploadedDate?: Timestamp;
}>;

/**
* @deprecated since owner re-structuring into their own collection
*/
export type IDeprecatedEntityCf = Modify<IDeprecatedEntity, {
  formationDate: Timestamp | null;
  dueDate: Timestamp | null;
  lastAcceptedFiling: Timestamp | null;
  lastModifiedDate: Timestamp | null;
  bulkUploadedDate?: Timestamp;
}>;

/**
 * Determines if an entity is using the deprecated entity structure.
 *
 * @deprecated This type guard is for legacy entity structures.
 * Use the newer entity structure with ownerRelationships instead.
 *
 * @param {IEntityCf | IDeprecatedEntityCf} entity - The entity to check
 * @return {boolean} Returns true if the entity uses the deprecated structure with non-empty owners property
 */
export const isDeprecatedEntityCf = (entity: IEntityCf | IDeprecatedEntityCf): entity is IDeprecatedEntityCf => {
  return (
    Object.prototype.hasOwnProperty.call(entity, "owners") &&
    Object.keys((entity as IDeprecatedEntityCf).owners).length !== 0
  );
}

export interface IProEntityCf extends IEntityCf {
  managingOrgId: UUID;
  groupId: UUID;
  groupName: string;
}

export const isProEntityCf = (entity: IEntityCf): entity is IProEntityCf => {
  return entity.managingOrgId !== null && !!entity.managingOrgId;
}

export interface IHandedOffEntityCf extends IProEntityCf {
  handedOff: true;
}

export const isHandedOffEntityCf = (entity: IEntityCf): entity is IHandedOffEntityCf => {
  return isProEntityCf(entity) && entity.handedOff;
}

export interface IFileEntityCf extends IEntityCf {
  managingOrgId: null;
  handedOff: false;
  groupId: null;
  groupName: null;
}

export const isFileEntityCf = (entity: IEntityCf): entity is IFileEntityCf => {
  return !entity.managingOrgId && !entity.handedOff;
}
